import type { HTMLAttributes } from "react";
import { leadCatBrand, productBrand, type LeadCatProduct } from "../../brand/brandConfig";
import styles from "./BrandLogo.module.css";

export type BrandLogoVariant = "lockup" | "symbol" | "icon";
export type BrandLogoSize = "small" | "medium" | "large";
export type BrandLogoSurface = "light" | "dark";

interface BrandLogoProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  product?: LeadCatProduct;
  variant?: BrandLogoVariant;
  size?: BrandLogoSize;
  surface?: BrandLogoSurface;
}

const assetForVariant: Record<Exclude<BrandLogoVariant, "lockup">, string> = {
  symbol: leadCatBrand.assets.symbol,
  icon: leadCatBrand.assets.icon180,
};

export function BrandLogo({
  product = "crm",
  variant = "lockup",
  size = "medium",
  surface = "light",
  className = "",
  ...props
}: BrandLogoProps) {
  const brand = productBrand(product);
  const classes = [
    styles.logo,
    styles[variant],
    styles[size],
    styles[`surface${surface === "dark" ? "Dark" : "Light"}`],
    className,
  ].filter(Boolean).join(" ");

  return (
    <div
      {...props}
      className={classes}
      data-brand-product={product}
      data-brand-variant={variant}
    >
      {variant === "lockup" ? (
        <>
          <span className={styles.symbolFrame} aria-hidden="true">
            <img src={leadCatBrand.assets.symbol} alt="" draggable={false} decoding="async" />
          </span>
          <span className={styles.wordmarkFrame}>
            <img src={leadCatBrand.assets.wordmark} alt={brand.name} draggable={false} decoding="async" />
          </span>
          <span className={styles.descriptor} aria-hidden="true">{brand.descriptor}</span>
        </>
      ) : (
        <span className={styles.artFrame}>
          <img src={assetForVariant[variant]} alt={brand.name} draggable={false} decoding="async" />
        </span>
      )}
    </div>
  );
}
